import { observer } from 'mobx-react-lite';
import Popup from '../../../../../components/common-components/pop-up/Popup';
import { Building2 } from 'lucide-react';
import { useStore } from '../../../../../hooks/useStore';
import { Form, Formik } from 'formik';
import { FormikTextField } from '../../../../../components/form-components/common-components/text-field/FormikTextField';
import FormPopupFooter from '../../../../../components/form-components/common-components/FormPopupFooter';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import styles from './EditHospital.module.css';

type EditHospitalFormValues = {
  id: number;
  hospitalName: string;
  hospitalAddress: string;
  isHospitalClosed: boolean;
};

const validationSchema = Yup.object({
  hospitalName: Yup.string()
    .required('Hastane adı zorunludur')
    .min(2, 'Hastane adı en az 2 karakter olmalıdır'),
  hospitalAddress: Yup.string(),
});

const EditHospital = () => {
  const { popupStore, hospitalStore, uiStore, authStore } = useStore();
  const isAdmin = authStore.isAdmin;

  const initialValues = {
    id: hospitalStore.selectedHospitalToEdit?.id || 0,
    hospitalName: hospitalStore.selectedHospitalToEdit?.hospitalName || '',
    hospitalAddress: hospitalStore.selectedHospitalToEdit?.hospitalLocation || '',
    isHospitalClosed: hospitalStore.selectedHospitalToEdit?.isHospitalClosed || false,
  };

  const handleSubmit = async (values: EditHospitalFormValues) => {
    try {
      if (!values.id) {
        toast.error('Hastane ID bulunamadı');
        return;
      }

      uiStore.setLoading(true);
      const response = await hospitalStore.updateHospital(values.id, {
        id: values.id,
        hospitalName: values.hospitalName,
        hospitalLocation: values.hospitalAddress || undefined,
        isHospitalClosed: values.isHospitalClosed,
      });

      if (response.error === false) {
        toast.success('Hastane başarıyla güncellendi.');
        popupStore.setIsEditHospitalOpen(false);
        hospitalStore.setSelectedHospitalToEdit(null);
        hospitalStore.refreshGrid();
      } else {
        toast.error('Hastane güncellenirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Hastane güncellenirken hata:', error);
      toast.error('Hastane güncellenirken bir hata oluştu');
    } finally {
      uiStore.setLoading(false);
    }
  };

  const handleClose = () => {
    popupStore.setIsEditHospitalOpen(false);
    hospitalStore.setSelectedHospitalToEdit(null);
    toast.dismiss();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ setFieldValue, values }) => (
        <Popup
          isOpen={popupStore.isEditHospitalOpen}
          onClose={handleClose}
          title="Hastane Düzenle"
          description="Hastane bilgilerini güncelleyin"
          variant="success"
          icon={<Building2 size={22} strokeWidth={2} />}
          footer={() => (
            <FormPopupFooter onClose={handleClose} saveLabel="Güncelle" submittingLabel="Güncelleniyor..." />
          )}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="hospitalName"
                  label="Hastane Adı"
                  placeholder="Hastane adını giriniz"
                  startIcon={<Building2 size={18} strokeWidth={2} />}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="hospitalAddress"
                  label="Hastane Adresi"
                  placeholder="Hastane adresini giriniz"
                />
              </div>

              {isAdmin && (
                <div className={styles.inputContainer}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={values.isHospitalClosed}
                      onChange={e => setFieldValue('isHospitalClosed', e.target.checked)}
                      className={styles.checkbox}
                    />
                    Hastane Kapalı
                  </label>
                </div>
              )}
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(EditHospital);
